import { Type } from 'class-transformer'
import { BaseEntity, type Searchable } from './base'
import type { PagingQuery } from './common'
import type { User } from './user'

export type MassiveEmailPhase = 'draft' | 'sent' | 'closed'

export interface MassiveEmailMetadata {
  [key: string]: string
}

export interface MassiveEmailAnswer {
  label: string
  value: string
  [x: string]: string
}

export class MassiveEmailRow extends BaseEntity implements Searchable {
  displayName!: string
  createdAt!: string
  sentAt?: string
  closedAt?: string
  templateFile!: string
  redirectUrl!: string
  typeId!: string
  createdById!: string
  sentById!: string
  closedById!: string

  type!: {
    color: string
    displayName: string
  }

  public get searchString() {
    return this.displayName
  }

  public get phase(): MassiveEmailPhase {
    if (this.closedAt) {
      return 'closed'
    } else if (this.sentAt) {
      return 'sent'
    } else {
      return 'draft'
    }
  }

  public get phaseSeverity() {
    if (this.phase === 'closed') {
      return 'contrast'
    } else if (this.phase === 'draft') {
      return 'warning'
    } else {
      return 'success'
    }
  }

  public get isEditable() {
    return this.phase === 'draft'
  }
}

export class MassiveEmail extends MassiveEmailRow {
  metadata!: MassiveEmailMetadata
  answers!: MassiveEmailAnswer[]

  subject!: string | null
  createdBy!: User
  sentBy?: User
  closedBy?: User
}

export interface GetMassiveEmails {
  massiveEmails: MassiveEmailRow[]
}
export interface GetMassiveEmailDetails {
  massiveEmail: MassiveEmail
}

export interface GetMassiveEmailTemplates {
  massiveEmailTemplates: string[]
}

export class NewMassiveEmail {
  typeId!: string
  displayName!: string
  templateFile!: string
  redirectUrl?: string | null
}

export class UpdateMassiveEmail {
  typeId?: string
  displayName?: string
  templateFile?: string
  redirectUrl?: string | null
}

export class UpdateMassiveEmailMetadata {
  subject?: string | null
  metadata?: MassiveEmailMetadata
  answers?: MassiveEmailAnswer[]
}

export interface MassiveEmailWritten {
  massiveEmail: BaseEntity
}

class ReceiverPractice extends BaseEntity {
  displayName!: string
  archiveNumber!: string
}

export class BaseMassiveEmailReceiver extends BaseEntity {
  emailAddress!: string
  metadata!: Record<string, string>
  massiveEmailId!: string
  practiceId!: string
  sentAt?: string
}

export class MassiveEmailReceiver extends BaseMassiveEmailReceiver {
  @Type(() => ReceiverPractice)
  practice!: ReceiverPractice
}

export interface GetMassiveEmailReceivers {
  receivers: MassiveEmailReceiver[]
  hasNextPage: boolean
}

export interface GetMassiveEmailReceiversQuery extends PagingQuery {
  searchString?: string
}

export class NewMassiveEmailReceivers {
  practiceId!: string
  emailAddresses!: string[]
}

export class ImportMassiveEmailReceivers {
  replace!: boolean
}

export interface MassiveEmailReceiversAdded {
  receivers: MassiveEmailReceiver[]
}

export interface GetMassiveEmailPreview {
  subject: string
  renderedTemplate: string
  rawTemplate: string
}

export interface SendMassiveEmail {
  sent: boolean
}

export interface GetMassiveEmailResponsesQuery extends PagingQuery {
  searchString?: string
  response?: string
}

export class MassiveEmailResponse extends BaseEntity {
  receiver!: MassiveEmailReceiver
  receiverId!: string
  createdAt!: string
  response!: string
}

export interface GetMassiveEmailResponses {
  responses: MassiveEmailResponse[]
  hasNextPage: boolean
}

export type MassiveEmailResponsesReportFormat = 'xlsx' | 'csv'

export interface MassiveEmailStats {
  totalReceivers: number
  totalResponses: number | null
}

export interface GetMassiveEmailStats {
  stats: MassiveEmailStats
}
